"use client";

/**
 * The transcript + notes workspace: file strip on top, two panes below —
 * resizable side-by-side on wide viewports, tab-switched on narrow ones.
 * Until notes are engaged (generated, generating, or started blank) the
 * notes pane stays a fixed narrow rail so the transcript gets the width.
 * Never prerendered (the flow only reaches "working" client-side), so the
 * media-query hook's false SSR snapshot is irrelevant here.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Plus } from "lucide-react";
import { NotesPanel } from "@/components/flow/notes-panel";
import { TranscriptPanel } from "@/components/flow/transcript-panel";
import { resetSession, useSessionState } from "@/lib/client/session-store";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn, formatDuration } from "@/lib/utils";

const SPLIT_MIN_PCT = 28;
const SPLIT_MAX_PCT = 72;
/** Width of the collapsed notes rail (empty state before notes exist). */
const NOTES_RAIL_PX = 340;
/** Width of the divider / spacer between the panes (w-3.5). */
const DIVIDER_PX = 14;

export function Workspace() {
  const t = useTranslations("Flow.workspace");
  const session = useSessionState();
  const wide = useMediaQuery("(min-width: 880px)");
  const [activeTab, setActiveTab] = useState<"transcript" | "notes">(
    "transcript",
  );
  const [splitPct, setSplitPct] = useState(52);
  const [dragging, setDragging] = useState(false);
  const splitRef = useRef<HTMLDivElement>(null);
  /** Owns the window drag listeners; aborting is the one teardown path. */
  const dragAbortRef = useRef<AbortController | null>(null);

  // The listeners live on `window` — they must not survive an unmount
  // mid-drag.
  useEffect(() => () => dragAbortRef.current?.abort(), []);

  const notesEngaged = session.notesDoc !== null || session.notesGenerating;

  const lastSegment = session.segments[session.segments.length - 1];
  const durationMs = lastSegment?.endMs ?? lastSegment?.startMs ?? null;

  function handleStartOver() {
    if (
      session.hasUnexportedContent &&
      !window.confirm(t("discardConfirm"))
    ) {
      return;
    }
    resetSession();
  }

  function handleDividerPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    const container = splitRef.current;
    if (!container) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    dragAbortRef.current?.abort();
    const controller = new AbortController();
    dragAbortRef.current = controller;
    // Window listeners see EVERY pointer — a second touch elsewhere must
    // neither steer nor end this drag.
    const pointerId = event.pointerId;
    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      // Re-read the rect each move — the window can resize mid-drag
      // (e.g. iPad split-view adjusted with the other hand).
      const rect = container.getBoundingClientRect();
      const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.max(SPLIT_MIN_PCT, Math.min(SPLIT_MAX_PCT, pct)));
    };
    const endDrag = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;
      setDragging(false);
      controller.abort();
    };
    const opts = { signal: controller.signal };
    window.addEventListener("pointermove", onMove, opts);
    window.addEventListener("pointerup", endDrag, opts);
    // Touch scrolling & co. end a captured drag with pointercancel, not
    // pointerup — without this the move listener leaks and keeps resizing.
    window.addEventListener("pointercancel", endDrag, opts);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none items-center justify-between gap-4 border-b bg-card px-4 py-2.5 sm:px-7">
        <div className="flex min-w-0 items-baseline gap-3">
          <span className="truncate text-[14.5px] font-semibold">
            {session.audioFile?.name}
          </span>
          {durationMs !== null && (
            <span className="flex-none font-mono text-[11.5px] text-muted-foreground">
              {formatDuration(durationMs)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3.5">
          <span className="hidden items-center gap-1.5 whitespace-nowrap text-[11.5px] text-muted-foreground md:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            {t("inTabOnly")}
          </span>
          <button
            type="button"
            onClick={handleStartOver}
            className="flex h-[34px] items-center gap-[7px] whitespace-nowrap rounded-[9px] border border-input px-3.5 text-[13px] font-medium transition-colors hover:bg-accent"
          >
            <Plus className="h-[13px] w-[13px]" />
            {t("startOver")}
          </button>
        </div>
      </div>

      {!wide && (
        <div className="flex flex-none gap-1 px-5 pt-2.5">
          {(["transcript", "notes"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={cn(
                "h-9 flex-1 rounded-[9px] border text-[13px] font-semibold transition-colors",
                activeTab === tab
                  ? "border-input bg-card text-foreground"
                  : "border-transparent text-muted-foreground",
              )}
            >
              {t(tab)}
            </button>
          ))}
        </div>
      )}

      <div
        ref={splitRef}
        className={cn(
          "flex min-h-0 flex-1",
          wide ? "p-[18px_24px_24px]" : "p-[12px_20px_20px]",
        )}
      >
        <div
          className={cn(
            "min-h-0 min-w-0 flex-col",
            !wide && activeTab !== "transcript" ? "hidden" : "flex",
            !wide && "flex-1",
            // Animate the rail expanding into the split, but never fight
            // the pointer while the divider is being dragged.
            wide && !dragging && "transition-[flex-basis] duration-300",
          )}
          style={
            wide
              ? {
                  flex: notesEngaged
                    ? `0 0 calc(${splitPct}% - ${DIVIDER_PX / 2}px)`
                    : `0 0 calc(100% - ${NOTES_RAIL_PX + DIVIDER_PX}px)`,
                }
              : undefined
          }
        >
          <TranscriptPanel />
        </div>

        {wide && notesEngaged && (
          <div
            onPointerDown={handleDividerPointerDown}
            className="flex w-3.5 flex-none cursor-col-resize touch-none items-center justify-center self-stretch"
            role="separator"
            aria-orientation="vertical"
          >
            <div className="h-11 w-[3px] rounded-sm bg-input" />
          </div>
        )}
        {/* No divider handle while the notes side is just the empty rail —
            there is nothing worth resizing yet. */}
        {wide && !notesEngaged && <div className="w-3.5 flex-none" />}

        <div
          className={cn(
            "min-h-0 min-w-0 flex-1 flex-col",
            !wide && activeTab !== "notes" ? "hidden" : "flex",
          )}
        >
          <NotesPanel />
        </div>
      </div>
    </div>
  );
}
